import React, { useCallback, useState } from 'react';
import isEmpty from 'lodash/isEmpty';

import Grid from '@xcritical/grid';
import Button from '@xcritical/button';
import { Pagination } from '@xcritical/pagination';
import Modal from '@xcritical/modal';

import { Spacing } from '@crm-framework/spacing';
import { Confirm } from '@crm-framework/confirm';
import { Loader } from '@crm-framework/loader';

import { INotificationGridProps } from './types';
import { notificationGridTheme } from './theme';
import {
  NotificationsButtonGroup,
  NotificationsFooter,
  NotificationsGridLoaderWrapper,
  NotificationsGridWrapper,
  NotificationsPaginationWrapper,
} from './styles';
import { GRID_COLUMNS } from './gridColumns';

export const NotificationGrid: React.FC<INotificationGridProps> = ({
  isLoading,
  items,
  selectedIds,
  notMarkedAsReadSelectedIds,
  page,
  pageSize,
  totalCount,
  onChangePage,
  onChangePageSize,
  onMarkAsRead,
  onDelete,
}) => {
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const showDeleteConfirm = useCallback(() => {
    setIsConfirmDeleteOpen(true);
  }, []);

  const onDeleteConfirmed = useCallback(() => {
    setIsConfirmDeleteOpen(false);
    onDelete();
  }, [onDelete]);

  const onDeleteConfirmCancel = useCallback(() => {
    setIsConfirmDeleteOpen(false);
  }, []);

  return (
    <>
      <NotificationsGridWrapper>
        {isLoading && (
          <NotificationsGridLoaderWrapper>
            <Loader />
          </NotificationsGridLoaderWrapper>
        )}

        <Grid
          shouldFitContainer
          disableSelect
          shouldMovingColumns={false}
          shouldChangeColumnsWidth={false}
          rowHeight={30}
          columns={GRID_COLUMNS}
          items={items}
          theme={notificationGridTheme}
        />
      </NotificationsGridWrapper>

      <NotificationsFooter>
        <NotificationsButtonGroup>
          <Spacing mr={15}>
            <Button
              appearance="primary"
              disabled={isEmpty(notMarkedAsReadSelectedIds) || isLoading}
              onClick={onMarkAsRead}>
              Mark as read
            </Button>
          </Spacing>
          <Button
            ghost
            appearance="primary"
            disabled={isEmpty(selectedIds) || isLoading}
            onClick={showDeleteConfirm}>
            Delete
          </Button>
        </NotificationsButtonGroup>

        <NotificationsPaginationWrapper>
          <Pagination
            showTotals
            visibleRange={3}
            disabled={isLoading}
            currentPage={page}
            total={totalCount}
            pageSize={pageSize}
            pageSizeProps={{
              menuPlacement: 'auto',
              components: {
                IndicatorSeparator: null,
              },
            }}
            onChangePage={onChangePage}
            onChangePageSize={onChangePageSize}
          />
        </NotificationsPaginationWrapper>
      </NotificationsFooter>

      <Modal
        name="notifications-delete-confirmation"
        title="Notifications delete confirmation"
        isOpen={isConfirmDeleteOpen}
        onModalCancel={onDeleteConfirmCancel}>
        <Confirm onOk={onDeleteConfirmed} onCancel={onDeleteConfirmCancel}>
          Do you really want to delete selected notifications?
        </Confirm>
      </Modal>
    </>
  );
};
